import React from 'react';

import {
  DEMAND_UNIT_LABELS,
  SORT_ASC,
  SORT_COLUMNS,
  demandBlockReason,
  measureOf,
  rowDemand,
  sortNeedsDemand,
} from './insulationPickerView.js';
import { formatMeasure, formatMoney, formatVolume, shortName } from './insulationPickerFormat.js';

/**
 * Заголовок сортируемого столбца.
 *
 * «Итого» и «Сумма» считаются от потребности, и пока она не задана, сортировать
 * по ним нечего: у всех строк там прочерк. Такой заголовок гасится и говорит
 * почему — молча неработающая кнопка читалась бы как поломка.
 */
const SortableTh = ({ column, label, title, sort, sortColumn, onSort, hasDemand }) => {
  const blocked = sortNeedsDemand(column) && !hasDemand;
  const isActive = sortColumn === column;

  return (
    <th scope="col" className="ins-pick__th ins-pick__th--sortable">
      <button
        type="button"
        className={`ins-pick__sort${isActive ? ' is-active' : ''}`}
        onClick={() => onSort(column)}
        disabled={blocked}
        title={blocked ? 'Сначала укажите потребность' : title}
      >
        {label}
        <span aria-hidden="true">{isActive ? (sort === SORT_ASC ? ' ↑' : ' ↓') : ' ↕'}</span>
      </button>
    </th>
  );
};

/**
 * Таблица подбора: товар, характеристика, применения, цена за м³ и за упаковку,
 * количество и суммарный объём под него.
 *
 * Разметка повторяет сравнение производств в газобетоне: строки собраны сеткой
 * и на узком экране разворачиваются в карточки (см. insulationPicker.css).
 */
const InsulationPickerTable = ({
  rows,
  best,
  measure,
  sort,
  sortColumn,
  onSort,
  demand,
  onSelect,
  activeFilterCount = 0,
}) => {
  const hasDemand = Boolean(demand && String(demand.value || '').trim());

  return (
  <table className="ins-pick__table">
    <thead>
      <tr>
        <th scope="col" className="ins-pick__th">Товар</th>
        <th scope="col" className="ins-pick__th">{measure.title}</th>
        <th scope="col" className="ins-pick__th">Тип применения</th>
        <SortableTh
          column={SORT_COLUMNS.PRICE_PER_M3}
          label="Цена за м³"
          title="Сортировка по цене за кубометр"
          sort={sort}
          sortColumn={sortColumn}
          onSort={onSort}
          hasDemand={hasDemand}
        />
        <th scope="col" className="ins-pick__th">Цена за упаковку</th>
        <th scope="col" className="ins-pick__th">Упаковок</th>
        <SortableTh
          column={SORT_COLUMNS.TOTAL_VOLUME}
          label="Итого"
          title="Сортировка по объёму под потребность"
          sort={sort}
          sortColumn={sortColumn}
          onSort={onSort}
          hasDemand={hasDemand}
        />
        <SortableTh
          column={SORT_COLUMNS.TOTAL_PRICE}
          label="Сумма"
          title="Сортировка по сумме под потребность"
          sort={sort}
          sortColumn={sortColumn}
          onSort={onSort}
          hasDemand={hasDemand}
        />
      </tr>
    </thead>
    <tbody>
      {rows.length === 0 && (
        <tr className="ins-pick__row ins-pick__row--empty">
          <td className="ins-pick__cell" colSpan={8}>
            {/* Раньше здесь была одна фраза «товаров нет», и она не помогала:
                отсечь мог фильтр, выбранный давно и уехавший из видимой части
                списка применений. Теперь причина названа. */}
            {activeFilterCount > 0
              ? `Ни один товар не проходит сразу по всем выбранным фильтрам (${activeFilterCount}). Снимите лишние в строке «Фильтры» выше.`
              : 'Под этот вид материала нет ни одной позиции со свежей ценой — в подбор попадают только цены не старше 14 дней'}
          </td>
        </tr>
      )}

      {rows.map((row) => {
        const totals = rowDemand(row, demand);
        return (
          <tr key={row.id} className={`ins-pick__row${row === best ? ' is-best' : ''}`}>
            <td className="ins-pick__cell ins-pick__cell--name">
              {row === best && <span className="ins-pick__flag">↓ Самое дешёвое за м³</span>}
              <span className="ins-pick__name">{shortName(row.name)}</span>
              <span className="ins-pick__sub">
                {[row.manufacturer, row.source].filter(Boolean).join(' · ')}
              </span>
              {row.thickness > 0 && (
                <span className="ins-pick__sub">
                  Толщина {row.thickness} мм · упаковка {formatVolume(row.volume)} м³
                </span>
              )}
              {/* Дата цены стоит у каждой строки. Старше двух недель в подбор
                  теперь не попадает вовсе, но менеджеру всё равно важно видеть,
                  сегодняшняя это цена или прошлонедельная. */}
              {row.priceUpdateDate && (
                <span className="ins-pick__sub">Цена от {row.priceUpdateDate}</span>
              )}
              {row.isVolumeDisputed && (
                <span className="ins-pick__warn">
                  Объём упаковки в справочнике и в названии расходятся — проверьте
                </span>
              )}
            </td>

            <td className="ins-pick__cell ins-pick__cell--num" data-label={measure.title}>
              {formatMeasure(measureOf(row), measure.unit)}
            </td>

            <td className="ins-pick__cell ins-pick__cell--apps" data-label="Тип применения">
              {row.applicationLabels.length > 0 ? row.applicationLabels.join(', ') : '—'}
            </td>

            <td className="ins-pick__cell ins-pick__cell--money" data-label="Цена за м³">
              {row.pricePerM3 === null ? '—' : `${formatMoney(row.pricePerM3)} ₽`}
            </td>

            <td className="ins-pick__cell ins-pick__cell--money" data-label="Цена за упаковку">
              {row.price === null ? '—' : `${formatMoney(row.price)} ₽`}
            </td>

            {/* Количество не вводится по строке: потребность одна на таблицу,
                а строка показывает, во сколько целых упаковок она у неё
                укладывается. */}
            <td className="ins-pick__cell ins-pick__cell--num" data-label="Упаковок">
              {totals && totals.packages ? totals.packages : '—'}
            </td>

            <td className="ins-pick__cell ins-pick__cell--total-amount" data-label="Итого">
              {totals && totals.packages ? (
                <>
                  <span>{formatVolume(totals.totalVolume)} м³</span>
                  {totals.totalArea > 0 && (
                    <span className="ins-pick__sub">{formatVolume(totals.totalArea)} м²</span>
                  )}
                  {/* Кратность вслух: менеджер просил 1000 м², получит 1009 —
                      это должно быть видно до отправки КП, а не после. */}
                  {totals.overshoot > 0.0005 && (
                    <span className="ins-pick__sub ins-pick__sub--over">
                      +{formatVolume(totals.overshoot)} {DEMAND_UNIT_LABELS[totals.unit]} сверх нужного
                    </span>
                  )}
                </>
              ) : (
                <span className="ins-pick__sub ins-pick__sub--why">
                  {totals ? demandBlockReason(row, demand) : 'укажите потребность'}
                </span>
              )}
            </td>

            <td className="ins-pick__cell ins-pick__cell--total" data-label="Сумма">
              <span>{totals && totals.totalPrice > 0 ? `${formatMoney(totals.totalPrice)} ₽` : '—'}</span>
              <button
                type="button"
                className="ins-pick__take"
                disabled={!totals || !totals.packages}
                title={totals && totals.packages ? undefined : 'Сначала укажите потребность'}
                onClick={() => onSelect(row, totals.packages)}
              >
                В расчёт
              </button>
            </td>
          </tr>
        );
      })}
    </tbody>
  </table>
  );
};

export default InsulationPickerTable;
